import * as React from 'react';

import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Tailwind-merge does not recognize the `tw:` prefix, so `DialogContent`'s own
 * centering, `grid`, `gap-4`, `rounded-lg` and `p-6` survive a plain override
 * and have to be beaten with `!important`.
 */
const SURFACE =
  'dark tw:!inset-0 tw:!top-0 tw:!left-0 tw:!flex tw:!h-screen tw:!max-h-none tw:!w-screen tw:!max-w-none tw:!translate-x-0 tw:!translate-y-0 tw:!flex-col tw:!gap-0 tw:!rounded-none tw:!border-0 tw:overflow-hidden tw:bg-background tw:!p-0 tw:text-foreground';

export type FullScreenDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  /**
   * Takes the title's place in the header row, for a dialog whose own fields
   * say what it is. `title` is still required and stays on the dialog for
   * screen readers.
   */
  header?: React.ReactNode;
  /** Sits to the left of the title, e.g. a Cancel button. */
  headerLeading?: React.ReactNode;
  /** Full-width band under the header, e.g. a wizard step bar. */
  nav?: React.ReactNode;
  /** Persistent left column, e.g. a wizard stepper. */
  rail?: React.ReactNode;
  /** Extra classes on the rail, for a wider or always-visible stepper. */
  railClassName?: string;
  /** Action buttons, pinned below the scroll region. */
  footer?: React.ReactNode;
  /** Extra classes on the scrolling body (e.g. full-bleed chart steps). */
  bodyClassName?: string;
  formProps?: React.ComponentProps<'form'>;
  children: React.ReactNode;
};

/**
 * Covers the whole viewport, including the app header, because the dialog
 * portals to `document.body`. Header, nav, rail and footer stay put while the
 * body scrolls.
 */
export function FullScreenDialog({
  open,
  onOpenChange,
  title,
  description,
  header,
  headerLeading,
  nav,
  rail,
  railClassName,
  footer,
  bodyClassName,
  formProps,
  children,
}: FullScreenDialogProps) {
  const regions = (
    <>
      <DialogHeader
        className={cn(
          'tw:shrink-0 tw:border-b tw:border-border tw:px-6 tw:py-4 tw:pr-16',
          // twMerge cannot see through the `tw:` prefix, so DialogHeader's own
          // column layout has to be beaten rather than replaced.
          (headerLeading || header) && 'tw:!flex-row tw:gap-3',
          // Fields in the header sit label-above-input, so a leading button
          // lines up with their bottom edge rather than their middle.
          headerLeading && (header ? 'tw:!items-end' : 'tw:!items-center')
        )}
      >
        {headerLeading}
        {header ? (
          <>
            <DialogTitle className="tw:sr-only">{title}</DialogTitle>
            {description ? (
              <DialogDescription className="tw:sr-only">
                {description}
              </DialogDescription>
            ) : null}
            <div className="tw:min-w-0 tw:flex-1">{header}</div>
          </>
        ) : (
          <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-1">
            <DialogTitle className="tw:truncate tw:text-base">
              {title}
            </DialogTitle>
            {description ? (
              <DialogDescription>{description}</DialogDescription>
            ) : null}
          </div>
        )}
      </DialogHeader>

      {nav ? (
        <div className="tw:shrink-0 tw:border-b tw:border-border tw:px-6 tw:py-3">
          {nav}
        </div>
      ) : null}

      <div className="tw:flex tw:min-h-0 tw:flex-1">
        {rail ? (
          <div
            className={cn(
              'tw:hidden tw:w-64 tw:shrink-0 tw:overflow-y-auto tw:border-r tw:border-border tw:px-6 tw:py-6 tw:md:block',
              railClassName
            )}
          >
            {rail}
          </div>
        ) : null}
        <div
          className={cn(
            'tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:px-6 tw:py-6',
            bodyClassName
          )}
        >
          {children}
        </div>
      </div>

      {footer ? (
        <DialogFooter className="tw:shrink-0 tw:flex-row tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:px-6 tw:py-4">
          {footer}
        </DialogFooter>
      ) : null}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={SURFACE}>
        {formProps ? (
          <form
            {...formProps}
            className={cn(
              'tw:flex tw:min-h-0 tw:flex-1 tw:flex-col',
              formProps.className
            )}
          >
            {regions}
          </form>
        ) : (
          regions
        )}
      </DialogContent>
    </Dialog>
  );
}
